import React from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import FormHelperText from "@mui/material/FormHelperText";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MKButton from "components/MKButton";
import MKInput from "components/MKInput";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormLabel from "@mui/material/FormLabel";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Material Kit 2 PRO React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images
import bgImage from "assets/images/content-5.jpg";

//other
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PublicIcon from "@mui/icons-material/Public";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import Autocomplete from "@mui/material/Autocomplete";

const ReviewItem = ({ label, value, highlight }) => {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;

  return (
    <MKBox mb={1.2}>
      <MKTypography variant="caption" color="text.secondary">
        {label}
      </MKTypography>
      <MKTypography fontWeight={highlight ? 600 : 500} sx={highlight ? { color: "#700808" } : {}}>
        {Array.isArray(value) ? value.join(", ") : value}
      </MKTypography>
    </MKBox>
  );
};

function EmployerInfoStep({ formData, setFormData, errors = {} }) {
  const languageOptions = ["Swahili", "English", "Kinyarwanda", "Luganda", "French"];

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      employer: {
        ...prev.employer,
        [field]: e.target.value,
      },
    }));
  };

  const handleLanguages = (e, value) => {
    setFormData((prev) => ({
      ...prev,
      employer: {
        ...prev.employer,
        languages: value,
      },
    }));
  };

  return (
    <>
      <MKBox mb={4}>
        {/* Section Title */}
        <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MKTypography variant="body2" mb={2} color="text.secondary">
            Fill in your details below — from Contact to Location and Language Preferences
          </MKTypography>
          <MKTypography
            component={Link}
            to="/services/booking"
            variant="body2"
            mb={2}
            fontWeight="medium"
            sx={{
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              textDecorationColor: "#700808",
              color: "#700808",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
                textDecorationColor: "#700808",
              },
            }}
          >
            All services
          </MKTypography>
        </MKBox>
        <MKTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          Fields marked with <span style={{ color: "#700808", fontWeight: 600 }}>*</span> are
          required
        </MKTypography>
        {/* Modern Separator */}
        <MKBox
          sx={{
            height: "2px",
            width: "100%",
            background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
            borderRadius: "4px",
            mb: 3,
          }}
        />
        {/* Form */}
        <Grid container spacing={3}>
          {/* Full Name */}
          <Grid item xs={12} md={6}>
            <MKInput
              value={formData.employer.fullName}
              onChange={handleChange("fullName")}
              variant="outlined"
              label="Full Name"
              placeholder="Enter your full name"
              size="large"
              fullWidth
              startAdornment={<PersonIcon sx={{ mr: 1, color: "gray" }} />}
              required
              error={Boolean(errors.fullName)}
              helperText={errors.fullName}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={12} md={6}>
            <MKInput
              value={formData.employer.phone}
              onChange={handleChange("phone")}
              variant="outlined"
              label="Phone Number (WhatsApp preferred)"
              placeholder="+250..."
              size="large"
              fullWidth
              startAdornment={<PhoneIcon sx={{ mr: 1, color: "gray" }} />}
              required
              error={Boolean(errors.phone)}
              helperText={errors.phone}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <MKInput
              type="email"
              value={formData.employer.email}
              onChange={handleChange("email")}
              variant="outlined"
              label="Email"
              placeholder="Enter email"
              size="large"
              fullWidth
              startAdornment={<EmailIcon sx={{ mr: 1, color: "gray" }} />}
              required
              error={Boolean(errors.email)}
              helperText={errors.email}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>

          {/* Nationality */}
          <Grid item xs={12} md={6}>
            <MKInput
              value={formData.employer.nationality}
              onChange={handleChange("nationality")}
              variant="outlined"
              label="Nationality"
              placeholder="Enter nationality"
              size="large"
              fullWidth
              startAdornment={<PublicIcon sx={{ mr: 1, color: "gray" }} />}
              required
              error={Boolean(errors.nationality)}
              helperText={errors.nationality}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>

          {/* Location */}
          <Grid item xs={12} md={6}>
            <MKInput
              value={formData.employer.location}
              onChange={handleChange("location")}
              variant="outlined"
              label="Current Location (Country / City / Area)"
              placeholder="e.g. Rwanda, Kigali, Kacyiru"
              size="large"
              fullWidth
              startAdornment={<LocationOnIcon sx={{ mr: 1, color: "gray" }} />}
              required
              error={Boolean(errors.location)}
              helperText={errors.location}
              FormHelperTextProps={{
                sx: { color: "#700808" },
              }}
            />
          </Grid>

          {/* Preferred Language */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={languageOptions}
              value={formData.employer.languages}
              onChange={handleLanguages}
              renderInput={(params) => (
                <MKInput
                  {...params}
                  variant="outlined"
                  placeholder="our communication language - Select"
                />
              )}
            />
          </Grid>
        </Grid>
      </MKBox>
    </>
  );
}

function HouseholdStep({ formData, setFormData, errors = {} }) {
  const homeTypes = ["Apartment", "House", "Gated Compound", "Other"];

  // Handle home type selection
  const handleHomeTypeChange = (type) => (e) => {
    const value = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      household: {
        ...prev.household,
        homeType: value ? type : "",
      },
    }));
  };

  // Number of children
  const handleChildrenCountChange = (e) => {
    const value = Number(e.target.value);

    setFormData((prev) => ({
      ...prev,
      household: {
        ...prev.household,
        childrenCount: value,
        // clean dependent data if 0
        childrenAgeGroups: value > 0 ? prev.household.childrenAgeGroups : [],
        childrenNotes: value > 0 ? prev.household.childrenNotes : "",
      },
    }));
  };

  // Age group selection
  const handleAgeGroupChange = (group) => (e) => {
    const checked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      household: {
        ...prev.household,
        childrenAgeGroups: checked
          ? [...prev.household.childrenAgeGroups, group]
          : prev.household.childrenAgeGroups.filter((g) => g !== group),
      },
    }));
  };

  // Children notes
  const handleChildrenNotesChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      household: {
        ...prev.household,
        childrenNotes: e.target.value,
      },
    }));
  };

  // Handle additional notes
  const handleNotesChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      household: {
        ...prev.household,
        notes: e.target.value,
      },
    }));
  };

  return (
    <MKBox mb={4}>
      {/* Section Title */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" color="text.secondary">
          Fill in your household details — home type, number of children, and any additional notes.
        </MKTypography>
        <MKTypography
          component={Link}
          to="/services/booking"
          variant="body2"
          mb={2}
          fontWeight="medium"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textDecorationColor: "#700808",
            color: "#700808",
            cursor: "pointer",
            "&:hover": { opacity: 0.8 },
          }}
        >
          All services
        </MKTypography>
      </MKBox>
      <MKTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
        Fields marked with <span style={{ color: "#700808", fontWeight: 600 }}>*</span> are required
      </MKTypography>
      {/* Modern Separator */}
      <MKBox
        sx={{
          height: "2px",
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
          borderRadius: "4px",
          mb: 3,
        }}
      />
      {/* Form */}
      <Grid container spacing={3}>
        {/* Home Type */}
        <Grid item xs={12}>
          <MKTypography variant="button" mb={1}>
            Type of Home*
          </MKTypography>
          <Grid container spacing={1}>
            {homeTypes.map((type) => (
              <Grid item key={type}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.household.homeType === type}
                      onChange={handleHomeTypeChange(type)}
                      color="primary"
                      required
                      error={Boolean(errors.homeType)}
                      helperText={errors.homeType}
                      FormHelperTextProps={{
                        sx: { color: "#700808" },
                      }}
                    />
                  }
                  label={type}
                />
              </Grid>
            ))}
            {/* Optional text input if "Other" */}
            {formData.household.homeType === "Other" && (
              <Grid item xs={12} md={4}>
                <MKInput
                  value={formData.household.otherHomeType || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      household: { ...prev.household, otherHomeType: e.target.value },
                    }))
                  }
                  placeholder="Specify other"
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
            )}
          </Grid>
          {errors.homeType && (
            <FormHelperText sx={{ color: "#700808" }}>{errors.homeType}</FormHelperText>
          )}
        </Grid>

        {/* Number of Children */}
        <Grid item xs={12}>
          {/* Age groups */}
          <MKTypography variant="caption" color="text.secondary" mb={1}>
            Children Age Groups (select all that apply)*
          </MKTypography>

          <Grid container spacing={1}>
            {[
              { label: "School-age (5–12 years)", value: "school" },
              { label: "Teenager (13+ years)", value: "teen" },
            ].map((group) => (
              <Grid item xs={12} sm={6} md={3} key={group.value}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.household.childrenAgeGroups?.includes(group.value)}
                      onChange={handleAgeGroupChange(group.value)}
                      color="primary"
                      required
                    />
                  }
                  label={group.label}
                />
              </Grid>
            ))}
          </Grid>
          {/* Children count + Notes row */}
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12} md={4}>
              <MKInput
                type="number"
                label="Children in Household"
                value={formData.household.childrenCount || ""}
                onChange={handleChildrenCountChange}
                inputProps={{ min: 0 }}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <MKInput
                label="Additional Notes (optional)"
                placeholder="e.g. newborn care, twins, special attention needed"
                value={formData.household.childrenNotes}
                onChange={handleChildrenNotesChange}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Additional Notes */}
        <Grid item xs={12}>
          <MKTypography variant="button" mb={1}>
            Additional Notes*
          </MKTypography>
          <MKInput
            value={formData.household.notes}
            onChange={handleNotesChange}
            multiline
            rows={4}
            placeholder="Any additional information..."
            fullWidth
            required
            error={Boolean(errors.notes)}
            helperText={errors.notes}
            FormHelperTextProps={{
              sx: { color: "#700808" },
            }}
          />
        </Grid>
      </Grid>
    </MKBox>
  );
}

function CareNeedsStep({ formData, setFormData, errors = {} }) {
  // --- Handlers ---
  const handleText = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      care: {
        ...prev.care,
        [field]: e.target.value,
      },
    }));
  };

  return (
    <MKBox mb={4}>
      {/* Section Title */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" mb={2} color="text.secondary">
          Provide information about children, elderly, and pets that require special attention.
        </MKTypography>
        <MKTypography
          component={Link}
          to="/services/booking"
          variant="body2"
          mb={2}
          fontWeight="medium"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textDecorationColor: "#700808",
            color: "#700808",
            fontWeight: 500,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
              textDecorationColor: "#700808",
            },
          }}
        >
          All services
        </MKTypography>
      </MKBox>
      <MKTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
        Fields marked with <span style={{ color: "#700808", fontWeight: 600 }}>*</span> are required
      </MKTypography>
      {/* Modern Separator */}
      <MKBox
        sx={{
          height: "2px",
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
          borderRadius: "4px",
          mb: 3,
        }}
      />
      <Grid container spacing={3}>
        {/* ROW 1 */}
        <Grid item xs={12} md={6}>
          <MKTypography variant="button">
            Children with any condition or mobility status
          </MKTypography>

          <MKBox display="flex" gap={3}>
            <FormControlLabel
              control={<Checkbox checked={formData.care.childrenSpecial === true} />}
              onChange={() =>
                setFormData((p) => ({
                  ...p,
                  care: { ...p.care, childrenSpecial: true },
                }))
              }
              label="Yes"
            />

            <FormControlLabel
              control={<Checkbox checked={formData.care.childrenSpecial === false} />}
              onChange={() =>
                setFormData((p) => ({
                  ...p,
                  care: { ...p.care, childrenSpecial: false },
                }))
              }
              label="No"
            />
          </MKBox>
        </Grid>

        <Grid item xs={12} md={6}>
          <MKTypography variant="button">Daily medication required? </MKTypography>

          <MKBox display="flex" gap={3}>
            <FormControlLabel
              control={<Checkbox checked={formData.care.childrenMedication === true} />}
              onChange={() =>
                setFormData((p) => ({
                  ...p,
                  care: { ...p.care, childrenMedication: true },
                }))
              }
              label="Yes"
            />

            <FormControlLabel
              control={<Checkbox checked={formData.care.childrenMedication === false} />}
              onChange={() =>
                setFormData((p) => ({
                  ...p,
                  care: { ...p.care, childrenMedication: false },
                }))
              }
              label="No"
            />
          </MKBox>
        </Grid>

        {/* ROW 2 */}
        <Grid item xs={12} md={6}>
          <MKTypography variant="button">Any Elderly Persons at Home? </MKTypography>

          <MKBox display="flex" gap={3}>
            <FormControlLabel
              control={<Checkbox checked={formData.care.elderlyPresent === true} />}
              onChange={() =>
                setFormData((p) => ({
                  ...p,
                  care: { ...p.care, elderlyPresent: true },
                }))
              }
              label="Yes"
            />

            <FormControlLabel
              control={<Checkbox checked={formData.care.elderlyPresent === false} />}
              onChange={() =>
                setFormData((p) => ({
                  ...p,
                  care: { ...p.care, elderlyPresent: false },
                }))
              }
              label="No"
            />
          </MKBox>
        </Grid>

        <Grid item xs={12} md={6}>
          <MKTypography variant="button">Any Pets? </MKTypography>

          <MKBox display="flex" gap={3}>
            <FormControlLabel
              control={<Checkbox checked={formData.care.petsPresent === true} />}
              onChange={() =>
                setFormData((p) => ({
                  ...p,
                  care: { ...p.care, petsPresent: true },
                }))
              }
              label="Yes"
            />

            <FormControlLabel
              control={<Checkbox checked={formData.care.petsPresent === false} />}
              onChange={() =>
                setFormData((p) => ({
                  ...p,
                  care: { ...p.care, petsPresent: false },
                }))
              }
              label="No"
            />
          </MKBox>
        </Grid>

        {/* CONDITIONAL INPUTS */}
        {formData.care.childrenSpecial && (
          <Grid item xs={12}>
            <MKInput
              value={formData.care.childrenDetails}
              onChange={handleText("childrenDetails")}
              placeholder="Age(s) and mobility status"
              fullWidth
            />
          </Grid>
        )}

        {formData.care.childrenMedication && (
          <Grid item xs={12}>
            <MKInput
              value={formData.care.childrenMedicationDesc}
              onChange={handleText("childrenMedicationDesc")}
              placeholder="Describe medication"
              fullWidth
            />
          </Grid>
        )}
        {/* ADDITIONAL INFORMATION */}
        <Grid item xs={12}>
          <MKTypography variant="button" mb={1}>
            Additional Information*
          </MKTypography>

          <MKInput
            multiline
            rows={4}
            placeholder="Any other information we should know (medical history, routines, preferences, concerns, etc.)"
            value={formData.care.additionalInfo || ""}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                care: {
                  ...p.care,
                  additionalInfo: e.target.value,
                },
              }))
            }
            fullWidth
            required
            error={Boolean(errors.additionalInfo)}
            helperText={errors.additionalInfo}
            FormHelperTextProps={{
              sx: { color: "#700808" },
            }}
          />
        </Grid>
      </Grid>
    </MKBox>
  );
}

function ChildcareTasksStep({ formData, setFormData }) {
  // --- Childcare tasks ---
  const tasks = [
    "School pick-up/drop-off",
    "Helping with homework (basic)",
    "Bathing and dressing kids",
    "Putting children to bed (read a book)",
  ];

  // --- Handlers ---
  const handleTask = (task, field) => () => {
    setFormData((prev) => ({
      ...prev,
      tasks: {
        ...prev.tasks,
        childcare: {
          ...prev.tasks.childcare,
          [task]: field === "yes" ? true : false,
        },
      },
    }));
  };

  return (
    <MKBox mb={4}>
      {/* Section Title */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" mb={2} color="text.secondary">
          Tick the tasks required and mark Yes or No next to each
        </MKTypography>
        <MKTypography
          component={Link}
          to="/services/booking"
          variant="body2"
          mb={2}
          fontWeight="medium"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textDecorationColor: "#700808",
            color: "#700808",
            fontWeight: 500,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
              textDecorationColor: "#700808",
            },
          }}
        >
          All services
        </MKTypography>
      </MKBox>
      {/* Modern Separator */}
      <MKBox
        sx={{
          height: "2px",
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
          borderRadius: "4px",
          mb: 3,
        }}
      />
      <Grid container spacing={3}>
        {tasks.map((task) => (
          <Grid item xs={12} key={task}>
            <MKBox display="flex" alignItems="center" flexWrap="wrap">
              <MKTypography sx={{ flex: 1 }}>{task}</MKTypography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.childcare?.[task] === true}
                    onChange={handleTask(task, "yes")}
                    color="primary"
                  />
                }
                label="Yes"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.childcare?.[task] === false}
                    onChange={handleTask(task, "no")}
                    color="secondary"
                  />
                }
                label="No"
              />
            </MKBox>
          </Grid>
        ))}
      </Grid>
    </MKBox>
  );
}
function CookingTasksStep({ formData, setFormData }) {
  // --- Cooking tasks ---
  const tasks = [
    "Cooking (local/EAC dishes)",
    "Cooking (international dishes, but basic)",
    "Grocery shopping or market errands",
    "Washing dishes (organize)",
    "Food prep assistance",
  ];

  // --- Handlers ---
  const handleTask = (task, field) => () => {
    setFormData((prev) => ({
      ...prev,
      tasks: {
        ...prev.tasks,
        cooking: {
          ...prev.tasks.cooking,
          [task]: field === "yes" ? true : false,
        },
      },
    }));
  };

  return (
    <MKBox mb={4}>
      {/* Section Title */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" mb={2} color="text.secondary">
          Tick the tasks required and mark Yes or No next to each
        </MKTypography>
        <MKTypography
          component={Link}
          to="/services/booking"
          variant="body2"
          mb={2}
          fontWeight="medium"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textDecorationColor: "#700808",
            color: "#700808",
            fontWeight: 500,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
              textDecorationColor: "#700808",
            },
          }}
        >
          All services
        </MKTypography>
      </MKBox>
      {/* Modern Separator */}
      <MKBox
        sx={{
          height: "2px",
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
          borderRadius: "4px",
          mb: 3,
        }}
      />
      <Grid container spacing={3}>
        {tasks.map((task) => (
          <Grid item xs={12} key={task}>
            <MKBox display="flex" alignItems="center" flexWrap="wrap">
              <MKTypography sx={{ flex: 1 }}>{task}</MKTypography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.cooking?.[task] === true}
                    onChange={handleTask(task, "yes")}
                    color="primary"
                  />
                }
                label="Yes"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.cooking?.[task] === false}
                    onChange={handleTask(task, "no")}
                    color="secondary"
                  />
                }
                label="No"
              />
            </MKBox>
          </Grid>
        ))}
      </Grid>
    </MKBox>
  );
}
function CleaningTasksStep({ formData, setFormData }) {
  // --- Cleaning tasks ---
  const tasks = [
    "Sweeping and mopping floors",
    "Dusting furniture and entire house",
    "Laundry (hand wash)",
    "Laundry (using machine)",
    "Ironing clothes",
    "Organizing wardrobes",
  ];

  // --- Handlers ---
  const handleTask = (task, field) => () => {
    setFormData((prev) => ({
      ...prev,
      tasks: {
        ...prev.tasks,
        cleaning: {
          ...prev.tasks.cleaning,
          [task]: field === "yes" ? true : false,
        },
      },
    }));
  };

  return (
    <MKBox mb={4}>
      {/* Section Title */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" mb={2} color="text.secondary">
          Tick the tasks required and mark Yes or No next to each
        </MKTypography>
        <MKTypography
          component={Link}
          to="/services/booking"
          variant="body2"
          mb={2}
          fontWeight="medium"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textDecorationColor: "#700808",
            color: "#700808",
            fontWeight: 500,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
              textDecorationColor: "#700808",
            },
          }}
        >
          All services
        </MKTypography>
      </MKBox>
      {/* Modern Separator */}
      <MKBox
        sx={{
          height: "2px",
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
          borderRadius: "4px",
          mb: 3,
        }}
      />
      <Grid container spacing={3}>
        {tasks.map((task) => (
          <Grid item xs={12} key={task}>
            <MKBox display="flex" alignItems="center" flexWrap="wrap">
              <MKTypography sx={{ flex: 1 }}>{task}</MKTypography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.cleaning?.[task] === true}
                    onChange={handleTask(task, "yes")}
                    color="primary"
                  />
                }
                label="Yes"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.cleaning?.[task] === false}
                    onChange={handleTask(task, "no")}
                    color="secondary"
                  />
                }
                label="No"
              />
            </MKBox>
          </Grid>
        ))}
      </Grid>
    </MKBox>
  );
}
function OtherTasksStep({ formData, setFormData }) {
  // --- Task categories ---
  const petCareTasks = ["Walking the dog", "Feeding pets (basic)", "Cleaning pet spaces"];

  const elderlySupportTasks = [
    "Companionship (basic)",
    "Assisting with walking or movement (basic)",
    "Administering medication (basic)",
    "Escorting to doctor/appointments (basic)",
  ];

  const miscTasks = [
    "Running errands (basic)",
    "Gatekeeping assistance (basic)",
    "Assisting guests during visits",
    "Watering plants (basic)",
    "Cleaning car(s)",
    "Taking out trash",
  ];

  // --- Handlers ---
  const handleTask = (category, task, field) => () => {
    setFormData((prev) => ({
      ...prev,
      tasks: {
        ...prev.tasks,
        [category]: {
          ...prev.tasks[category],
          [task]: field === "yes" ? true : false,
        },
      },
    }));
  };

  return (
    <MKBox mb={4}>
      {/* Section Title */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" mb={2} color="text.secondary">
          Tick the tasks required and mark Yes or No next to each
        </MKTypography>
        <MKTypography
          component={Link}
          to="/services/booking"
          variant="body2"
          mb={2}
          fontWeight="medium"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textDecorationColor: "#700808",
            color: "#700808",
            fontWeight: 500,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
              textDecorationColor: "#700808",
            },
          }}
        >
          All services
        </MKTypography>
      </MKBox>
      {/* Modern Separator */}
      <MKBox
        sx={{
          height: "2px",
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
          borderRadius: "4px",
          mb: 3,
        }}
      />
      <Grid container spacing={3}>
        {/* Pet Care */}
        <Grid item xs={12}>
          <MKTypography variant="h5">Pet Care</MKTypography>
        </Grid>

        {petCareTasks.map((task) => (
          <Grid item xs={12} key={task}>
            <MKBox display="flex" alignItems="center" flexWrap="wrap">
              <MKTypography sx={{ flex: 1 }}>{task}</MKTypography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.petsElderly?.[task] === true}
                    onChange={handleTask("petsElderly", task, "yes")}
                    color="primary"
                  />
                }
                label="Yes"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.petsElderly?.[task] === false}
                    onChange={handleTask("petsElderly", task, "no")}
                    color="secondary"
                  />
                }
                label="No"
              />
            </MKBox>
          </Grid>
        ))}

        {/* Elderly Support */}
        <Grid item xs={12} mt={2}>
          <MKTypography variant="h5">Elderly Support (if applicable)</MKTypography>
        </Grid>

        {elderlySupportTasks.map((task) => (
          <Grid item xs={12} key={task}>
            <MKBox display="flex" alignItems="center" flexWrap="wrap">
              <MKTypography sx={{ flex: 1 }}>{task}</MKTypography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.petsElderly?.[task] === true}
                    onChange={handleTask("petsElderly", task, "yes")}
                    color="primary"
                  />
                }
                label="Yes"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.petsElderly?.[task] === false}
                    onChange={handleTask("petsElderly", task, "no")}
                    color="secondary"
                  />
                }
                label="No"
              />
            </MKBox>
          </Grid>
        ))}

        {/* Miscellaneous */}
        <Grid item xs={12} mt={2}>
          <MKTypography variant="h5">Miscellaneous</MKTypography>
        </Grid>

        {miscTasks.map((task) => (
          <Grid item xs={12} key={task}>
            <MKBox display="flex" alignItems="center" flexWrap="wrap">
              <MKTypography sx={{ flex: 1 }}>{task}</MKTypography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.petsElderly?.[task] === true}
                    onChange={handleTask("petsElderly", task, "yes")}
                    color="primary"
                  />
                }
                label="Yes"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tasks.petsElderly?.[task] === false}
                    onChange={handleTask("petsElderly", task, "no")}
                    color="secondary"
                  />
                }
                label="No"
              />
            </MKBox>
          </Grid>
        ))}
      </Grid>
    </MKBox>
  );
}

function ScheduleLanguageStep({ formData, setFormData, errors = {} }) {
  // --- Handlers ---
  const handleSchedule = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [field]: e.target.value,
      },
    }));
  };

  const handleWorkDays = (day) => (e) => {
    const checked = e.target.checked;
    setFormData((prev) => {
      const newWorkDays = checked
        ? [...prev.schedule.workDays, day]
        : prev.schedule.workDays.filter((d) => d !== day);
      return {
        ...prev,
        schedule: { ...prev.schedule, workDays: newWorkDays },
      };
    });
  };

  const handleLanguageLevel = (language) => (e) => {
    setFormData((prev) => ({
      ...prev,
      languageLevels: {
        ...prev.languageLevels,
        [language]: e.target.value,
      },
    }));
  };

  const liveOutShifts = [
    { label: "7:30 - 4:00 PM", start: "07:30", end: "16:00" },
    { label: "8:30 - 5:00 PM", start: "08:30", end: "17:00" },
    { label: "9:30 - 6:00 PM", start: "09:30", end: "18:00" },
    { label: "10:30 - 7:00 PM", start: "10:30", end: "19:00" },
    { label: "11:00 - 8:00 PM", start: "11:00", end: "20:00" },
    { label: "12:30 - 8:00 PM", start: "12:30", end: "20:00" },
  ];

  return (
    <MKBox mb={4}>
      {/* Section Title */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" mb={2} color="text.secondary">
          Provide information about housekeeper schedule and language preferences
        </MKTypography>
        <MKTypography
          component={Link}
          to="/services/booking"
          variant="body2"
          mb={2}
          fontWeight="medium"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textDecorationColor: "#700808",
            color: "#700808",
            fontWeight: 500,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
              textDecorationColor: "#700808",
            },
          }}
        >
          All services
        </MKTypography>
      </MKBox>
      <MKTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
        Fields marked with <span style={{ color: "#700808", fontWeight: 600 }}>*</span> are required
      </MKTypography>

      {/* Modern Separator */}
      <MKBox
        sx={{
          height: "2px",
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
          borderRadius: "4px",
          mb: 3,
        }}
      />
      <Grid container spacing={3}>
        {/* Live-In / Live-Out */}
        <Grid item xs={12}>
          <FormLabel component="legend">Live-In or Live-Out housekeeper?</FormLabel>
          <RadioGroup
            row
            value={formData.schedule.liveType || ""}
            onChange={handleSchedule("liveType")}
          >
            <FormControlLabel
              value="Live-In"
              control={<Radio />}
              label="Live-In (must have 1 day off, provide meal)"
            />
            <FormControlLabel value="Live-Out" control={<Radio />} label="Live-Out" />
          </RadioGroup>
          {formData.schedule.liveType === "Live-Out" && (
            <Grid item xs={12}>
              <MKTypography variant="subtitle1" gutterBottom>
                Select Working Hours
              </MKTypography>

              <RadioGroup
                value={formData.schedule.shiftPreset || ""}
                onChange={(e) => {
                  const selected = liveOutShifts.find((s) => s.label === e.target.value);

                  setFormData((prev) => ({
                    ...prev,
                    schedule: {
                      ...prev.schedule,
                      shiftPreset: selected.label,
                      startTime: selected.start,
                      endTime: selected.end,
                    },
                  }));
                }}
              >
                {liveOutShifts.map((shift) => (
                  <FormControlLabel
                    key={shift.label}
                    value={shift.label}
                    control={<Radio />}
                    label={shift.label}
                  />
                ))}
              </RadioGroup>
            </Grid>
          )}
        </Grid>

        {/* Working Hours */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MKTypography variant="subtitle1" gutterBottom>
                Working Hours Start
              </MKTypography>
              <MKInput
                type="time"
                value={formData.schedule.startTime || ""}
                onChange={handleSchedule("startTime")}
                fullWidth
                disabled={formData.schedule.liveType === "Live-Out"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MKTypography variant="subtitle1" gutterBottom>
                Working Hours End
              </MKTypography>
              <MKInput
                type="time"
                value={formData.schedule.endTime || ""}
                onChange={handleSchedule("endTime")}
                fullWidth
                disabled={formData.schedule.liveType === "Live-Out"}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Working Days */}
        <Grid item xs={12}>
          <FormLabel component="legend">Work Days</FormLabel>
          <Grid container spacing={2}>
            {/* Row 1 */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.schedule.workDays.includes("Monday–Friday")}
                    onChange={handleWorkDays("Monday–Friday")}
                  />
                }
                label="Monday–Friday"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.schedule.workDays.includes("Monday–Saturday")}
                    onChange={handleWorkDays("Monday–Saturday")}
                  />
                }
                label="Monday–Saturday"
              />
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.schedule.workDays.includes("Full Week")}
                    onChange={handleWorkDays("Full Week")}
                  />
                }
                label="Full Week"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Weekend & Meals Options */}
        <Grid item xs={12} mt={3}>
          <FormLabel component="legend">Additional Schedule Options</FormLabel>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.schedule.weekendWork || false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        schedule: { ...prev.schedule, weekendWork: e.target.checked },
                      }))
                    }
                  />
                }
                label="Is weekend work required?"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.schedule.provideMeals || false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        schedule: { ...prev.schedule, provideMeals: e.target.checked },
                      }))
                    }
                  />
                }
                label="Do you provide meals?"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {formData.schedule.liveType === "Live-In" && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.schedule.provideAccommodation || false}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          schedule: { ...prev.schedule, provideAccommodation: e.target.checked },
                        }))
                      }
                    />
                  }
                  label="Do you provide accommodation?"
                />
              )}
            </Grid>
          </Grid>
        </Grid>

        {/* Language Requirements */}
        <Grid item xs={12}>
          <FormLabel component="legend">Housekeeper Languages</FormLabel>
          <Grid container spacing={2}>
            {["English", "Swahili", "Luganda", "French", "Kinyarwanda"].map((lang) => (
              <Grid item xs={12} md={6} key={lang}>
                <MKTypography variant="subtitle2" sx={{ mb: 1 }}>
                  {lang}:
                </MKTypography>
                <RadioGroup
                  row
                  value={formData.languageLevels?.[lang] || ""}
                  onChange={handleLanguageLevel(lang)}
                >
                  <FormControlLabel value="Fluent" control={<Radio />} label="Fluent" />
                  <FormControlLabel value="Medium" control={<Radio />} label="Medium" />
                  <FormControlLabel value="Basic" control={<Radio />} label="Basic" />
                </RadioGroup>
              </Grid>
            ))}
          </Grid>
        </Grid>
        {/* Gender */}
        <Grid item xs={12}>
          <FormLabel component="legend">Preferred Gender *</FormLabel>

          <RadioGroup
            row
            value={formData.gender || ""}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                gender: e.target.value,
              }))
            }
            required
          >
            <FormControlLabel value="Male" control={<Radio />} label="Male" />
            <FormControlLabel value="Female" control={<Radio />} label="Female" />
          </RadioGroup>
          {errors.gender && (
            <MKTypography variant="caption" sx={{ color: "#700808" }}>
              {errors.gender}
            </MKTypography>
          )}
        </Grid>

        {/* Additional Notes */}
        <Grid item xs={12}>
          <MKTypography variant="subtitle1" fontWeight={600} gutterBottom>
            Additional Notes*
          </MKTypography>

          <MKTypography variant="body2" color="text.secondary" mb={1}>
            Include any special instructions, expectations, or important details.
          </MKTypography>

          <MKInput
            value={formData.schedule.notes || ""}
            onChange={handleSchedule("notes")}
            placeholder=""
            fullWidth
            multiline
            rows={3}
            required
            error={Boolean(errors.notes)}
            helperText={errors.notes}
            FormHelperTextProps={{
              sx: { color: "#700808" },
            }}
          />
        </Grid>
      </Grid>
    </MKBox>
  );
}
function ContractPeriodStep({ formData, setFormData, errors = {} }) {
  const contractOptions = ["Full Year", "6 Months", "3 Months", "1 Month", "Few Weeks"];

  const handleContractChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      contractPeriod: e.target.value,
    }));
  };

  return (
    <MKBox mb={4}>
      {/* Section Title */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" mb={2} color="text.secondary">
          Please select the contract duration you want for the housekeeper full package.
        </MKTypography>
        <MKTypography
          component={Link}
          to="/services/booking"
          variant="body2"
          mb={2}
          fontWeight="medium"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textDecorationColor: "#700808",
            color: "#700808",
            fontWeight: 500,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
              textDecorationColor: "#700808",
            },
          }}
        >
          All services
        </MKTypography>
      </MKBox>
      <MKTypography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
        Fields marked with <span style={{ color: "#700808", fontWeight: 600 }}>*</span> are required
      </MKTypography>
      {/* Modern Separator */}
      <MKBox
        sx={{
          height: "2px",
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
          borderRadius: "4px",
          mb: 3,
        }}
      />
      <FormLabel component="legend">Contract Duration</FormLabel>
      <RadioGroup value={formData.contractPeriod || ""} onChange={handleContractChange}>
        {contractOptions.map((option) => (
          <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
        ))}
      </RadioGroup>

      {/* Additional Notes */}
      <MKBox mt={3}>
        <FormLabel component="legend">Additional Notes*</FormLabel>

        <MKInput
          multiline
          rows={4}
          placeholder="Any extra instructions or requests you'd like us to know..."
          value={formData.additionalNotes}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              additionalNotes: e.target.value,
            }))
          }
          fullWidth
          required
          error={Boolean(errors.additionalNotes)}
          helperText={errors.additionalNotes}
          FormHelperTextProps={{
            sx: { color: "#700808" },
          }}
        />
      </MKBox>
      <MKTypography variant="caption" color="text.secondary">
        Example: preferred experience, special expectations, or any concerns.
      </MKTypography>
    </MKBox>
  );
}

function ReviewSubmitStep({ formData, setFormData }) {
  const reviewCardStyle = {
    p: 3,
    borderRadius: "1.5rem",
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,0.08)",
  };

  return (
    <MKBox mb={4}>
      {/* Header */}
      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="body2" color="text.secondary">
          Review your information before submitting your request.
        </MKTypography>

        <MKTypography
          component={Link}
          to="/services/booking"
          variant="body2"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            color: "#700808",
            cursor: "pointer",
            "&:hover": { opacity: 0.8 },
          }}
        >
          All services
        </MKTypography>
      </MKBox>

      {/* Separator */}
      <MKBox
        sx={{
          height: 2,
          width: "100%",
          background: "linear-gradient(90deg, #700808, rgba(112,8,8,0.2), transparent)",
          borderRadius: 2,
          mb: 3,
        }}
      />

      {/* REVIEW CARDS */}
      <Grid container spacing={3}>
        {/* Employer Info */}
        <Grid item xs={12} md={6}>
          <MKBox sx={reviewCardStyle}>
            <MKTypography fontWeight={600} mb={2}>
              Employer Information
            </MKTypography>

            <ReviewItem label="Full Name" value={formData.employer.fullName} />
            <ReviewItem label="Phone" value={formData.employer.phone} />
            <ReviewItem label="Email" value={formData.employer.email} />
            <ReviewItem label="Nationality" value={formData.employer.nationality} />
            <ReviewItem label="Location" value={formData.employer.location} />
            <ReviewItem label="Languages" value={formData.employer.languages} />
          </MKBox>
        </Grid>

        {/* Household Info */}
        <Grid item xs={12} md={6}>
          <MKBox sx={reviewCardStyle}>
            <MKTypography fontWeight={600} mb={2}>
              Household Details
            </MKTypography>

            <ReviewItem label="Home Type" value={formData.household.homeType} />
            <ReviewItem label="Children Count" value={formData.household.childrenCount} />
            <ReviewItem label="Children Age Groups" value={formData.household.childrenAgeGroups} />
            <ReviewItem label="Children Notes" value={formData.household.childrenNotes} />
            <ReviewItem label="Household Notes" value={formData.household.notes} />
          </MKBox>
        </Grid>

        {/* Care Needs */}
        <Grid item xs={12}>
          <MKBox sx={reviewCardStyle}>
            <MKTypography fontWeight={600} mb={2}>
              Special Care Needs
            </MKTypography>

            <ReviewItem
              label="Children Special Care"
              value={formData.care.childrenSpecial ? "Yes" : "No"}
            />
            <ReviewItem label="Children Details" value={formData.care.childrenDetails} />

            <ReviewItem
              label="Children Medication"
              value={formData.care.childrenMedication ? "Yes" : "No"}
            />
            <ReviewItem
              label="Medication Description"
              value={formData.care.childrenMedicationDesc}
            />

            <ReviewItem
              label="Elderly Present"
              value={formData.care.elderlyPresent ? "Yes" : "No"}
            />

            <ReviewItem label="Pets Present" value={formData.care.petsPresent ? "Yes" : "No"} />
          </MKBox>
        </Grid>

        {/* Schedule */}
        <Grid item xs={12}>
          <MKBox sx={reviewCardStyle}>
            <MKTypography fontWeight={600} mb={2}>
              Work Schedule
            </MKTypography>

            <ReviewItem label="Live Type" value={formData.schedule.liveType} />
            <ReviewItem label="Live out Time" value={formData.schedule.shiftPreset} />

            <ReviewItem
              label="Working Hours"
              value={
                formData.schedule.startTime && formData.schedule.endTime
                  ? `${formData.schedule.startTime} - ${formData.schedule.endTime}`
                  : ""
              }
            />

            <ReviewItem label="Work Days" value={formData.schedule.workDays} />

            <ReviewItem label="Weekend Work" value={formData.schedule.weekendWork ? "Yes" : "No"} />

            <ReviewItem
              label="Meals Provided"
              value={formData.schedule.provideMeals ? "Yes" : "No"}
            />

            <ReviewItem
              label="Accommodation Provided"
              value={formData.schedule.provideAccommodation ? "Yes" : "No"}
            />

            <ReviewItem label="Schedule Notes" value={formData.schedule.notes} />

            <MKTypography fontWeight={600} mb={2}>
              Language Levels
            </MKTypography>

            {Object.entries(formData.languageLevels).map(([lang, level]) => (
              <ReviewItem key={lang} label={lang} value={level} />
            ))}

            <ReviewItem label="Contract Duration" value={formData.contractPeriod} highlight />
            <ReviewItem label="Additional Notes" value={formData.additionalNotes} />
          </MKBox>
        </Grid>
      </Grid>

      {/* Acknowledgment */}
      <MKBox
        mt={4}
        p={2}
        borderRadius="1rem"
        sx={{
          backgroundColor: "rgba(112,8,8,0.05)",
          border: "1px solid rgba(112,8,8,0.15)",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.acknowledgment}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  acknowledgment: e.target.checked,
                }))
              }
            />
          }
          label="I confirm that the information provided above is correct and complete."
        />
      </MKBox>
    </MKBox>
  );
}
function Housekeeper() {
  const [activeStep, setActiveStep] = useState(0);
  const [_status, setStatus] = useState("idle");
  const [_isLoading, setIsLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const [openModal, setOpenModal] = useState(false);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      const { fullName, phone, email, nationality, location } = formData.employer;

      if (!fullName) newErrors.fullName = "Full name is required";
      if (!phone) newErrors.phone = "Phone number is required";
      if (!email) newErrors.email = "Email is required";
      if (!nationality) newErrors.nationality = "Nationality is required";
      if (!location) newErrors.location = "Location is required";
    }

    if (step === 1) {
      if (!formData.household.homeType) {
        newErrors.homeType = "Please select a home type";
      }

      if (!formData.household.notes?.trim()) {
        newErrors.notes = "Additional information is required";
      }
      if (!location) newErrors.location = "Location is required";
    }
    if (step === 2) {
      if (!formData.care.additionalInfo?.trim()) {
        newErrors.additionalInfo = "Additional information is required";
      }
    }

    if (step === 7) {
      if (!formData.gender) newErrors.gender = "Gender is required";
      if (!formData.schedule.liveType) newErrors.liveType = "Live type is required";
      if (!formData.schedule.notes?.trim()) {
        newErrors.notes = "Additional information is required";
      }
    }
    if (step === 8) {
      if (!formData.additionalNotes?.trim()) {
        newErrors.additionalNotes = "Additional information is required";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCloseModal = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenModal(false);
  };

  const steps = [
    "Employer Info",
    "Household",
    "Care Needs",
    "Childcare",
    "Cooking",
    "Cleaning",
    "Other Tasks",
    "Schedule & Language",
    "Contract Duration",
    "Review",
  ];

  const totalSteps = steps.length;
  const progress = ((activeStep + 1) / totalSteps) * 100;

  const handleNext = () => {
    const isValid = validateStep(activeStep);
    if (!isValid) return;

    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const [formData, setFormData] = useState({
    employer: {
      fullName: "",
      phone: "",
      email: "",
      nationality: "",
      location: "",
      languages: [],
    },
    household: {
      homeType: "",
      childrenCount: "",
      childrenAgeGroups: [],
      childrenNotes: "",
      notes: "",
    },

    care: {
      petsPresent: false,
      elderlyPresent: false,
      childrenSpecial: false,
      childrenMedication: false,
      childrenMedicationDesc: "",
      childrenDetails: "",
      additionalInfo: "",
    },

    tasks: {
      childcare: {},
      cooking: {},
      cleaning: {},
      petsElderly: {},
    },

    schedule: {
      liveType: "",
      shiftPreset: "",
      startTime: "",
      endTime: "",
      workDays: [],
      weekendWork: false,
      provideMeals: false,
      provideAccommodation: false,
      notes: "",
    },
    languageLevels: {
      English: "",
      Swahili: "",
      Luganda: "",
      French: "",
      Kinyarwanda: "",
    },

    contractPeriod: "",
    additionalNotes: "",
    gender: "",
    acknowledgment: false,
  });

  //normalize data before sending it
  const normalizeTasks = (tasksObj) =>
    Object.entries(tasksObj).map(([task, value]) => ({
      task,
      value,
    }));

  const buildPayload = (data) => ({
    ...data,

    tasks: {
      childcare: normalizeTasks(data.tasks.childcare),
      cooking: normalizeTasks(data.tasks.cooking),
      cleaning: normalizeTasks(data.tasks.cleaning),
      petsElderly: normalizeTasks(data.tasks.petsElderly),
    },

    submittedAt: new Date().toISOString(),
  });

  const handleSubmit = async () => {
    const isValid = validateStep(activeStep);
    if (!isValid) return;
    setStatus("loading");
    setIsLoading(true);

    try {
      const payload = buildPayload(formData);

      const res = await axios.post(`${SERVER_URL}/book/maid/housekeeper/`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.status === 200 && res.data === "Booked") {
        setAlertMsg("Thank you... we’ll get back to you soon");
        setStatus("success");

        // Show the modal
        setOpenModal(true);

        // Redirect after 3 seconds
        setTimeout(() => {
          navigate("/services/booking");
        }, 3000); // 3000ms = 3 seconds
      } else {
        setStatus("error");
        alert("Booking failed. Please try again.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      setStatus("error");
      alert("An error occurred while submitting your request.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <EmployerInfoStep formData={formData} setFormData={setFormData} errors={errors} />;

      case 1:
        return <HouseholdStep formData={formData} setFormData={setFormData} errors={errors} />;

      case 2:
        return <CareNeedsStep formData={formData} setFormData={setFormData} errors={errors} />;

      case 3:
        return <ChildcareTasksStep formData={formData} setFormData={setFormData} errors={errors} />;

      case 4:
        return <CookingTasksStep formData={formData} setFormData={setFormData} errors={errors} />;

      case 5:
        return <CleaningTasksStep formData={formData} setFormData={setFormData} errors={errors} />;

      case 6:
        return <OtherTasksStep formData={formData} setFormData={setFormData} errors={errors} />;

      case 7:
        return (
          <ScheduleLanguageStep formData={formData} setFormData={setFormData} errors={errors} />
        );

      case 8:
        return <ContractPeriodStep formData={formData} setFormData={setFormData} errors={errors} />;
      case 9:
        return <ReviewSubmitStep formData={formData} setFormData={setFormData} />;

      default:
        return null;
    }
  };

  return (
    <>
      <>
        <DefaultNavbar
          routes={routes}
          action={{
            type: "internal",
            route: "/services/booking",
            label: "BOOK NOW",
            color: "info",
          }}
          transparent
          light
        />
        <MKBox
          minHeight="50vh"
          width="100%"
          sx={{
            backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
              `${linearGradient(
                rgba(gradients.dark.main, 0.6),
                rgba(gradients.dark.state, 0.6)
              )}, url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "grid",
            placeItems: "center",
            py: 10,
          }}
        >
          <Container>
            <Grid
              container
              item
              xs={12}
              lg={8}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{ mx: "auto", textAlign: "center" }}
            >
              <MKTypography
                variant="h3"
                color="white"
                mb={2}
                sx={({ breakpoints, typography: { size } }) => ({
                  [breakpoints.down("md")]: {
                    fontSize: size["3xl"],
                  },
                })}
              >
                Housekeeper / HouseMaid / Maid
              </MKTypography>

              <MKTypography
                variant="body1"
                sx={({ breakpoints }) => ({
                  fontSize: "1rem", // smaller than default body1
                  color: "rgba(255, 255, 255, 0.75)", // light gray on dark bg
                  [breakpoints.down("md")]: {
                    fontSize: "0.95rem",
                  },
                  [breakpoints.down("sm")]: {
                    fontSize: "0.9rem",
                  },
                })}
              >
                Professional and reliable home care services, ensuring your living space stays
                clean, organized, and comfortable every day.
              </MKTypography>
            </Grid>
          </Container>
        </MKBox>

        {/* Wrapper for all cards */}
        <MKBox
          display="flex"
          flexDirection="column"
          alignItems="stretch" // <-- important
          gap={6}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 900, md: 1100, lg: 1200 }, // responsive width
            px: { xs: 2, sm: 3, md: 0 }, // side padding on small screens
            mx: "auto",
            mt: -8,
          }}
        >
          {/* Stepper Card */}
          <Card
            sx={{
              width: "100%",
              p: { xs: 3, md: 5 },
              borderRadius: "2rem",
              background: "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.85))",
              backdropFilter: "blur(18px) saturate(160%)",
              boxShadow: "0 24px 48px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            {/* ================= MOBILE STEP INDICATOR ================= */}
            <MKBox display={{ xs: "block", md: "none" }}>
              <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MKTypography fontWeight={600} sx={{ color: "#700808" }}>
                  Step {activeStep + 1}
                </MKTypography>
                <MKTypography variant="body2" color="text.secondary">
                  {totalSteps} steps
                </MKTypography>
              </MKBox>

              {/* Progress Bar */}
              <MKBox
                sx={{
                  width: "100%",
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: "rgba(112,8,8,0.12)",
                  overflow: "hidden",
                  mb: 1.5,
                }}
              >
                <MKBox
                  sx={{
                    width: `${progress}%`,
                    height: "100%",
                    background: "linear-gradient(90deg, #700808, #9e0b0b)",
                    transition: "width 0.5s cubic-bezier(.4,0,.2,1)",
                  }}
                />
              </MKBox>

              <MKTypography fontWeight={500}>{steps[activeStep]}</MKTypography>
            </MKBox>

            {/* ================= DESKTOP STEPPER ================= */}
            <MKBox display={{ xs: "none", md: "block" }} mb={4}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel
                      sx={{
                        "& .MuiStepIcon-root": {
                          color: "rgba(112,8,8,0.2)",
                          fontSize: "1.2rem",
                          transition: "all 0.3s ease",
                        },
                        "& .MuiStepIcon-root.Mui-active": {
                          color: "#700808",
                          fontSize: "1.4rem",
                        },
                        "& .MuiStepIcon-root.Mui-completed": {
                          color: "#700808",
                        },
                        "& .MuiStepLabel-label": {
                          fontSize: "0.85rem",
                          color: "rgba(0,0,0,0.5)",
                          mt: 1,
                        },
                        "& .MuiStepLabel-label.Mui-active": {
                          color: "#700808",
                          fontWeight: 600,
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </MKBox>

            {/* ================= STEP CONTENT ================= */}
            <MKBox mt={{ xs: 3, md: 2 }}>{renderStepContent(activeStep)}</MKBox>

            {/* ================= ACTION BUTTONS ================= */}
            <MKBox
              display="flex"
              justifyContent="space-between"
              mt={4}
              gap={2}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              {/* Back */}
              <MKButton
                fullWidth
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{
                  borderRadius: "1rem",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#700808",
                  backgroundColor: "rgba(112,8,8,0.06)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(112,8,8,0.12)",
                  },
                  "&:disabled": {
                    backgroundColor: "rgba(0,0,0,0.05)",
                    color: "rgba(112,8,8,0.3)",
                  },
                }}
              >
                Back
              </MKButton>

              {/* Next / Submit */}
              <MKButton
                fullWidth
                onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                sx={{
                  borderRadius: "1rem",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  background: "linear-gradient(90deg, #700808, #9e0b0b)",
                  color: "#fff",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 10px 20px rgba(112,8,8,0.35)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                }}
              >
                {activeStep === steps.length - 1 ? "Submit Request" : "Next"}
              </MKButton>
            </MKBox>
          </Card>

          {/* Contact Card */}
          <Card
            sx={{
              width: "100%",
              p: { xs: 4, md: 6 },
              borderRadius: "2rem",
              background: "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.9))",
              backdropFilter: "blur(14px) saturate(160%)",
              boxShadow: "0 30px 60px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <MKBox display="flex" flexDirection="column" alignItems="center" gap={6}>
              {/* Header */}
              <MKBox textAlign="center">
                <MKTypography variant="h4" fontWeight={600} sx={{ letterSpacing: "-0.5px" }}>
                  Other Ways to Reach Us
                </MKTypography>
                <MKTypography variant="body2" color="text.secondary" mt={1}>
                  We’re always available through the channels below
                </MKTypography>
              </MKBox>

              {/* Contact Info */}
              <Grid container spacing={4} justifyContent="center">
                {[
                  {
                    title: "Phone",
                    icon: <PhoneIcon />,
                    items: ["(+250) 795 305 418", "(+250) 796 716 708", "(+250) 796 505 768"],
                  },
                  {
                    title: "Email",
                    icon: <EmailIcon />,
                    items: [
                      "info@helpereverywhere.com",
                      "helpereverywheremail@gmail.com",
                      "support@helpereverywhere.com",
                    ],
                  },
                ].map((block, idx) => (
                  <Grid item xs={12} md={4} key={idx}>
                    <MKBox
                      p={4}
                      borderRadius="1.75rem"
                      sx={{
                        backgroundColor: "#fff",
                        boxShadow: "0 16px 30px rgba(0,0,0,0.06)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 24px 40px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      {/* Title */}
                      <MKBox display="flex" alignItems="center" gap={1.5} mb={3}>
                        <MKBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            backgroundColor: "rgba(112,8,8,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {React.cloneElement(block.icon, {
                            sx: { color: "#700808", fontSize: "1.4rem" },
                          })}
                        </MKBox>

                        <MKTypography variant="h6" fontWeight={600}>
                          {block.title}
                        </MKTypography>
                      </MKBox>

                      {/* Items */}
                      <MKBox display="flex" flexDirection="column" gap={1.5}>
                        {block.items.map((item, i) => (
                          <MKTypography
                            key={i}
                            sx={{
                              fontSize: "0.95rem",
                              fontWeight: 500,
                              color: "#700808",
                              backgroundColor: "rgba(112,8,8,0.05)",
                              px: 2,
                              py: 1,
                              borderRadius: "0.75rem",
                            }}
                          >
                            {item}
                          </MKTypography>
                        ))}
                      </MKBox>
                    </MKBox>
                  </Grid>
                ))}
              </Grid>

              {/* Social & Address */}
              <MKBox textAlign="center">
                <MKBox display="flex" justifyContent="center" gap={2} mb={2}>
                  {[
                    {
                      icon: <WhatsAppIcon />,
                      color: "#25D366",
                      link: "https://wa.me/250795305418",
                    },
                    {
                      icon: <FacebookIcon />,
                      color: "#1877F2",
                      link: "https://facebook.com/helpereverywhere",
                    },
                    {
                      icon: <InstagramIcon />,
                      color: "#E4405F",
                      link: "https://instagram.com/helpereverywhere",
                    },
                    {
                      icon: <TwitterIcon />,
                      color: "#1DA1F2",
                      link: "https://twitter.com/helpereverywhere",
                    },
                  ].map((social, idx) => (
                    <MKBox
                      key={idx}
                      component="a"
                      href={social.link}
                      target="_blank"
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "14px",
                        backgroundColor: "rgba(0,0,0,0.04)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: social.color,
                          transform: "translateY(-2px)",
                          "& svg": { color: "#fff" },
                        },
                      }}
                    >
                      {React.cloneElement(social.icon, {
                        sx: { fontSize: "1.5rem", color: social.color },
                      })}
                    </MKBox>
                  ))}
                </MKBox>

                <MKTypography variant="body2" color="text.secondary" fontWeight={500}>
                  KG 314 ST, Kigali, Rwanda
                </MKTypography>
              </MKBox>
            </MKBox>
          </Card>
        </MKBox>

        <MKBox pt={6} px={1} mt={6}>
          <DefaultFooter content={footerRoutes} />
        </MKBox>
        <Snackbar
          open={openModal}
          autoHideDuration={3000}
          onClose={handleCloseModal}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Alert
            onClose={handleCloseModal}
            severity="success"
            sx={{
              backgroundColor: "#7008084d",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "0.75rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            {alertMsg}
          </Alert>
        </Snackbar>
      </>
    </>
  );
}

export default Housekeeper;
